import React, { useEffect, useState } from 'react';
import { Button, DatePicker, Input, Select, Space } from 'antd';
import dayjs from 'dayjs';
import type { WorklogFilterState, WorklogFilters as WorklogFiltersType } from '../../types/worklog';
import { WORKLOG_TASK_TYPE_LABELS } from '../../types/worklog';

type Props = {
  filters: WorklogFiltersType;
  onChange: (next: Partial<WorklogFilterState>) => void;
};

const WorklogFilters: React.FC<Props> = ({ filters, onChange }) => {
  // 搜索框是唯一有本地状态的控件：输入时不能每敲一个字就往服务端跑一趟，
  // 回车或点按钮才提交。其余控件选中即提交。
  const [search, setSearch] = useState(filters.search ?? '');

  // 服务端回显的筛选条件变了（比如点了「清空」），把输入框同步回去。
  useEffect(() => {
    setSearch(filters.search ?? '');
  }, [filters.search]);

  const hasAnyFilter =
    !!filters.task_type || !!filters.working_directory || !!filters.search ||
    !!filters.since || !!filters.until;

  return (
    <Space wrap className="mb-4">
      <Input.Search
        allowClear
        placeholder="搜标题或摘要"
        style={{ width: 240 }}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        onSearch={(value) => onChange({ search: value || null })}
      />

      <Select
        allowClear
        placeholder="类型"
        style={{ width: 120 }}
        value={filters.task_type ?? undefined}
        onChange={(value) => onChange({ task_type: value ?? null })}
        options={filters.options.task_types.map((type) => ({
          value: type,
          label: WORKLOG_TASK_TYPE_LABELS[type] ?? type,
        }))}
      />

      <Select
        allowClear
        showSearch
        placeholder="项目目录"
        style={{ width: 280 }}
        value={filters.working_directory ?? undefined}
        onChange={(value) => onChange({ working_directory: value ?? null })}
        options={filters.options.working_directories.map((dir) => ({
          value: dir,
          // 目录路径很长，下拉里只显示最后一段，hover 看全量
          label: dir.split('/').filter(Boolean).pop() ?? dir,
          title: dir,
        }))}
      />

      <DatePicker.RangePicker
        placeholder={['起始日', '截止日']}
        // 受控展示：回显的是服务端规范化后的 ISO8601 串，转成 dayjs 才能回填进
        // 控件——不然带 since/until 的分享链接打开时 picker 是空的，点「清空」后
        // 旧日期也不会跟着从框里消失。
        value={[
          filters.since ? dayjs(filters.since) : null,
          filters.until ? dayjs(filters.until) : null,
        ]}
        onChange={(_dates, [since, until]) =>
          onChange({ since: since || null, until: until || null })
        }
      />

      {hasAnyFilter && (
        <Button
          onClick={() =>
            onChange({
              task_type: null,
              working_directory: null,
              search: null,
              since: null,
              until: null,
            })
          }
        >
          清空
        </Button>
      )}
    </Space>
  );
};

export default WorklogFilters;
